// B1012: Users & roles — every row is the server's answer to `SHOW USERS` /
// `SHOW ROLES`, and every action is one of the auth statements of
// web/docs/sql.md § Users, roles and permissions, sent on the shared WS.
//
// Both listings are superuser-only. A non-root session sees the server's
// `permission denied` in the card, never a fake empty list.

import { useCallback, useEffect, useMemo, useState } from 'react';
import { I } from '../components/icons.jsx';
import * as users from '../data/users-store.js';
import { getUsername } from '../data/auth-store.js';
import { listGrants, rulesOfRole } from '../data/grants-store.js';
import { accessHref } from '../data/access-route.js';
import { getCurrentDb } from '../data/db-store.js';

function errText(e) { return e?.message || String(e); }

// The identities as the server lists them, plus one `reload` for every
// mutation to call. A mutation never patches the local list: the server's
// answer is the truth (a GRANT on a role that a peer just dropped must show
// the server's outcome, not the optimistic one).
function useIdentities() {
  const [state, setState] = useState({ users: null, roles: null, rules: null, error: null, loading: true });
  const reload = useCallback(async () => {
    setState((s) => ({ ...s, loading: true }));
    try {
      // B1319: the rules only feed the per-role count; a refusal leaves the
      // count out and never hides the users.
      const [u, r, g] = await Promise.all([users.listUsers(), users.listRoles(), listGrants().catch(() => null)]);
      setState({ users: u, roles: r, rules: g, error: null, loading: false });
    } catch (e) {
      setState((s) => ({ ...s, error: errText(e), loading: false }));
    }
  }, []);
  useEffect(() => { reload(); }, [reload]);
  return { ...state, reload };
}

// Run one server action, surface its error next to the form, then reload.
function useAction(reload) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const run = useCallback(async (fn) => {
    setBusy(true); setError(null);
    try { await fn(); await reload(); return true; }
    catch (e) { setError(errText(e)); return false; }
    finally { setBusy(false); }
  }, [reload]);
  return { busy, error, setError, run };
}

function Avatar({ name, index }) {
  const grads = [
    'linear-gradient(135deg, #00FFD1, #0099FF)',
    'linear-gradient(135deg, #7B68EE, #0099FF)',
    'linear-gradient(135deg, #FF9800, #00E676)',
  ];
  return (
    <div className="users-avatar" style={{ background: grads[index % grads.length] }}>
      {String(name).slice(0, 2).toUpperCase()}
    </div>
  );
}

// ── the New user form ────────────────────────────────────────────────────
function NewUserForm({ onSubmit, onCancel, busy, error }) {
  const [name, setName] = useState('');
  const [password, setPassword] = useState('');
  const ok = name.trim() && password;
  return (
    <form className="users-form" onSubmit={(e) => { e.preventDefault(); if (ok) onSubmit(name.trim(), password); }}>
      <input autoFocus placeholder="user name" value={name} onChange={(e) => setName(e.target.value)} spellCheck={false} disabled={busy} />
      <input type="password" placeholder="password" value={password} onChange={(e) => setPassword(e.target.value)} autoComplete="new-password" disabled={busy} />
      <button className="btn btn-primary btn-sm" type="submit" disabled={busy || !ok}>Create</button>
      <button className="btn btn-ghost btn-sm" type="button" onClick={onCancel} disabled={busy}>Cancel</button>
      {error && <span className="users-error">{error}</span>}
    </form>
  );
}

// ── the per-user editor: password, sessions, delete ──────────────────────
function UserEditor({ user, roles, onClose, reload }) {
  const { busy, error, run } = useAction(reload);
  const [password, setPassword] = useState('');
  const [confirmDrop, setConfirmDrop] = useState(false);
  const [note, setNote] = useState(null);
  const self = user.user === getUsername();

  async function setPw(e) {
    e.preventDefault();
    if (!password) return;
    if (await run(() => users.setPassword(user.user, password))) { setPassword(''); setNote('password updated'); }
  }
  async function revoke() {
    if (await run(() => users.revokeSessions(user.user))) setNote('sessions revoked');
  }
  async function drop() {
    if (await run(() => users.dropUser(user.user))) onClose();
  }
  async function toggleRole(role, member) {
    await run(() => (member ? users.revokeRole(role, user.user) : users.grantRole(role, user.user)));
  }

  return (
    <tr className="users-editor-row">
      <td colSpan={4}>
        <div className="users-editor">
          <form className="users-form" onSubmit={setPw}>
            <span className="users-label">Password</span>
            <input type="password" placeholder="new password" value={password} onChange={(e) => setPassword(e.target.value)} autoComplete="new-password" disabled={busy} />
            <button className="btn btn-sm" type="submit" disabled={busy || !password}>Set password</button>
          </form>
          <div className="users-form">
            <span className="users-label">Roles</span>
            {roles.length === 0 && <span className="users-muted">no roles yet</span>}
            {roles.map((r) => {
              const member = user.roles.includes(r.role);
              return (
                <label key={r.role} className={'users-check' + (member ? ' on' : '')}>
                  <input type="checkbox" checked={member} disabled={busy} onChange={() => toggleRole(r.role, member)} />
                  {r.role}
                </label>
              );
            })}
          </div>
          <div className="users-form">
            <span className="users-label">Sessions</span>
            <button className="btn btn-sm" type="button" onClick={revoke} disabled={busy} title={self ? 'This signs you out of every OTHER session; this one keeps its token.' : 'Sign this user out everywhere'}>
              Revoke sessions
            </button>
            <span className="users-label" style={{ marginLeft: 16 }}>Delete</span>
            {confirmDrop ? (
              <>
                <span className="users-error">Delete user {user.user}?</span>
                <button className="btn btn-sm users-danger" type="button" onClick={drop} disabled={busy}>Delete</button>
                <button className="btn btn-ghost btn-sm" type="button" onClick={() => setConfirmDrop(false)} disabled={busy}>Cancel</button>
              </>
            ) : (
              <button className="btn btn-sm" type="button" onClick={() => setConfirmDrop(true)} disabled={busy}>Delete user…</button>
            )}
          </div>
          {(error || note) && (
            <div className={error ? 'users-error' : 'users-muted'}>{error || note}</div>
          )}
        </div>
      </td>
    </tr>
  );
}

// ── the Roles card ───────────────────────────────────────────────────────
function RoleRow({ role, allUsers, rules, reload }) {
  const [open, setOpen] = useState(false);
  const [confirmDrop, setConfirmDrop] = useState(false);
  const [pick, setPick] = useState('');
  const { busy, error, run } = useAction(reload);
  const candidates = allUsers.filter((u) => !role.members.includes(u.user));

  async function add(e) {
    e.preventDefault();
    if (!pick) return;
    if (await run(() => users.grantRole(role.role, pick))) setPick('');
  }
  async function drop() {
    if (await run(() => users.dropRole(role.role))) setOpen(false);
  }

  return (
    <div className="users-role">
      <div className="users-role-head" onClick={() => setOpen(!open)}>
        <I.ChevRight size={12} style={{ transform: open ? 'rotate(90deg)' : 'none', transition: 'transform 120ms' }} />
        <span className="users-role-name">{role.role}</span>
        <span className="users-role-count">{role.members.length}</span>
      </div>
      {open && (
        <div className="users-role-body">
          <div className="users-form">
            <span className="users-muted">
              {rules == null ? 'Permissions' : `${ruleCount(rulesOfRole(rules, role.role).length)} in database ${getCurrentDb()}`}
            </span>
            <a className="btn btn-sm btn-ghost" href={accessHref('role', role.role)}><I.Lock size={12} /> Access</a>
          </div>
          {role.members.length === 0 && <div className="users-muted">no members</div>}
          {role.members.map((m) => (
            <div key={m} className="users-member">
              <span>{m}</span>
              <button className="icon-btn" type="button" title={`Remove ${m} from ${role.role}`} disabled={busy} onClick={() => run(() => users.revokeRole(role.role, m))}>
                <I.Close size={10} />
              </button>
            </div>
          ))}
          <form className="users-form" onSubmit={add}>
            <select value={pick} onChange={(e) => setPick(e.target.value)} disabled={busy || candidates.length === 0}>
              <option value="">{candidates.length ? 'add a member…' : 'every user is a member'}</option>
              {candidates.map((u) => <option key={u.user} value={u.user}>{u.user}</option>)}
            </select>
            <button className="btn btn-sm" type="submit" disabled={busy || !pick}>Add</button>
          </form>
          <div className="users-form">
            {confirmDrop ? (
              <>
                <span className="users-error">Delete role {role.role}?</span>
                <button className="btn btn-sm users-danger" type="button" onClick={drop} disabled={busy}>Delete</button>
                <button className="btn btn-ghost btn-sm" type="button" onClick={() => setConfirmDrop(false)} disabled={busy}>Cancel</button>
              </>
            ) : (
              <button className="btn btn-ghost btn-sm" type="button" onClick={() => setConfirmDrop(true)} disabled={busy}>Delete role…</button>
            )}
          </div>
          {error && <div className="users-error">{error}</div>}
        </div>
      )}
    </div>
  );
}

function ruleCount(n) { return `${n} rule${n === 1 ? '' : 's'}`; }

function NewRoleForm({ onSubmit, onCancel, busy, error }) {
  const [name, setName] = useState('');
  return (
    <form className="users-form" style={{ padding: '10px 16px', borderBottom: '1px solid var(--app-border)' }}
      onSubmit={(e) => { e.preventDefault(); if (name.trim()) onSubmit(name.trim()); }}>
      <input autoFocus placeholder="role name" value={name} onChange={(e) => setName(e.target.value)} spellCheck={false} disabled={busy} />
      <button className="btn btn-primary btn-sm" type="submit" disabled={busy || !name.trim()}>Create</button>
      <button className="btn btn-ghost btn-sm" type="button" onClick={onCancel} disabled={busy}>Cancel</button>
      {error && <span className="users-error">{error}</span>}
    </form>
  );
}

// ── the page ─────────────────────────────────────────────────────────────
export function UsersPage() {
  const ids = useIdentities();
  const [filter, setFilter] = useState('');
  const [adding, setAdding] = useState(false);
  const [addingRole, setAddingRole] = useState(false);
  const [editing, setEditing] = useState(null); // user name
  const newUser = useAction(ids.reload);
  const newRole = useAction(ids.reload);

  const shown = useMemo(() => {
    const list = ids.users ?? [];
    const q = filter.trim().toLowerCase();
    if (!q) return list;
    return list.filter((u) => u.user.toLowerCase().includes(q) || u.roles.some((r) => r.toLowerCase().includes(q)));
  }, [ids.users, filter]);

  async function createUser(name, password) {
    if (await newUser.run(() => users.createUser(name, password))) setAdding(false);
  }
  async function createRole(name) {
    if (await newRole.run(() => users.createRole(name))) setAddingRole(false);
  }

  const userCount = ids.users?.length;
  return (
    <div className="page">
      <div className="page-head">
        <div>
          <div className="crumbs">Admin · Users & roles</div>
          <h1>Users & roles</h1>
        </div>
        <div className="actions">
          <button className="btn btn-sm btn-ghost" type="button" onClick={ids.reload} disabled={ids.loading} title="Reload"><I.Refresh size={12} /> Refresh</button>
          <button className="btn btn-primary btn-sm" type="button" onClick={() => { setAdding(true); newUser.setError(null); }} disabled={adding}><I.Plus size={12} /> New user</button>
        </div>
      </div>
      <div className="users-grid">
        <div className="k-card">
          <div className="k-card-head">
            <span className="title">{userCount == null ? 'Users' : `${userCount} user${userCount === 1 ? '' : 's'}`}</span>
            <div className="actions">
              <div className="users-filter">
                <I.Search size={12} />
                <input placeholder="Filter…" value={filter} onChange={(e) => setFilter(e.target.value)} />
              </div>
            </div>
          </div>
          {adding && (
            <div style={{ padding: '10px 16px', borderBottom: '1px solid var(--app-border)' }}>
              <NewUserForm onSubmit={createUser} onCancel={() => setAdding(false)} busy={newUser.busy} error={newUser.error} />
            </div>
          )}
          {ids.error && <div className="users-error" style={{ padding: '12px 16px' }}>{ids.error}</div>}
          <table className="k-table">
            <thead><tr><th>User</th><th>Roles</th><th></th><th></th></tr></thead>
            <tbody>
              {ids.users == null && !ids.error && (
                <tr><td colSpan={4} className="users-muted">Loading…</td></tr>
              )}
              {ids.users != null && shown.length === 0 && (
                <tr><td colSpan={4} className="users-muted">{filter ? 'No user matches the filter.' : 'No users.'}</td></tr>
              )}
              {shown.map((u, i) => (
                <UserRows key={u.user} u={u} i={i} roles={ids.roles ?? []} editing={editing === u.user}
                  onEdit={() => setEditing(editing === u.user ? null : u.user)} onClose={() => setEditing(null)} reload={ids.reload} />
              ))}
            </tbody>
          </table>
        </div>
        <div className="k-card">
          <div className="k-card-head">
            <span className="title">{ids.roles == null ? 'Roles' : `${ids.roles.length} role${ids.roles.length === 1 ? '' : 's'}`}</span>
            <div className="actions">
              <button className="btn btn-sm btn-ghost" type="button" title="New role" onClick={() => { setAddingRole(true); newRole.setError(null); }} disabled={addingRole}><I.Plus size={12} /></button>
            </div>
          </div>
          {addingRole && <NewRoleForm onSubmit={createRole} onCancel={() => setAddingRole(false)} busy={newRole.busy} error={newRole.error} />}
          <div className="k-card-body" style={{ padding: 0 }}>
            {ids.roles == null && !ids.error && <div className="users-muted" style={{ padding: 12 }}>Loading…</div>}
            {ids.roles != null && ids.roles.length === 0 && <div className="users-muted" style={{ padding: 12 }}>No roles. A user gets permissions through roles: create one, add members, then give it access on the <a href={accessHref('role')}>Access</a> page.</div>}
            {(ids.roles ?? []).map((r) => <RoleRow key={r.role} role={r} allUsers={ids.users ?? []} rules={ids.rules} reload={ids.reload} />)}
          </div>
        </div>
      </div>
    </div>
  );
}

function UserRows({ u, i, roles, editing, onEdit, onClose, reload }) {
  const self = u.user === getUsername();
  return (
    <>
      <tr className={editing ? 'users-row-open' : undefined}>
        <td className="fg1">
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <Avatar name={u.user} index={i} />
            <div>
              <div style={{ color: 'var(--app-fg1)', fontWeight: 500 }}>{u.user}</div>
              {self && <div style={{ fontSize: 11, color: 'var(--app-fg3)' }}>you</div>}
            </div>
          </div>
        </td>
        <td>
          {u.roles.length === 0 && <span className="users-muted">—</span>}
          {u.roles.map((r) => <span key={r} className="pill" style={{ marginRight: 4 }}>{r}</span>)}
        </td>
        <td>{u.user === 'root' && <span className="pill cyan">superuser</span>}</td>
        <td style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
          <a className="btn btn-sm btn-ghost" href={accessHref('user', u.user)} title={`What ${u.user} can reach`}><I.Lock size={12} /> Access</a>
          <button className="btn btn-sm btn-ghost" type="button" onClick={onEdit} title="Edit"><I.Edit size={12} /> {editing ? 'Close' : 'Edit'}</button>
        </td>
      </tr>
      {editing && <UserEditor user={u} roles={roles} onClose={onClose} reload={reload} />}
    </>
  );
}
